import axios from 'axios';
import { api } from 'constants/constant';

const apiClient = axios.create({
  baseURL: api,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Country-Code': 'CA',
  },
  timeout: 30000,
});

// Attach Bearer token: secondary session (sessionStorage) takes priority,
// then falls back to tokenManager (localStorage) for primary sessions.
apiClient.interceptors.request.use(
  async (config) => {
    const isSecondary = !!sessionStorage.getItem('fm_access_token');
    if (isSecondary) {
      const tabToken = sessionStorage.getItem('fm_access_token');
      if (tabToken) {
        config.headers.Authorization = `Bearer ${tabToken}`;
      }
    } else {
      try {
        const tokenManager = (await import('services/auth/tokenManager')).default;
        const token = tokenManager.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // tokenManager not available yet
      }
    }

    // Attach tenant slug from hostname
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname || '';
      config.headers['X-Tenant-Host'] = hostname;

      const searchParams = new URLSearchParams(window.location.search);
      const slug = searchParams.get('slug');
      if (slug) {
        config.headers['X-Tenant-Slug'] = slug;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses — clear all session state and redirect to login
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const isTrulySecondary = sessionStorage.getItem('fm_secondary_session') === 'true';
      if (isTrulySecondary) {
        // Secondary session: only clear tab-scoped storage, leave primary intact
        sessionStorage.clear();
      } else {
        // Primary session: full cleanup
        try {
          const tokenManager = (await import('services/auth/tokenManager')).default;
          tokenManager.clearTokens();
        } catch {
          // ignore
        }
        try {
          const { clearCachedUserMetadata } = await import('auth/auth');
          clearCachedUserMetadata();
        } catch {
          // ignore
        }
        // NEVER localStorage.clear() — it is shared across tabs and would wipe
        // a different user's secondary session in another tab. We've already
        // cleared this primary session's own keys (tokens + metadata) above.
        sessionStorage.clear();
      }
      window.location.href = window.location.origin;
      // Return a never-resolving promise to prevent further processing
      // while the redirect is in progress.
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

export default apiClient;
